/**
 * ReviewPage Component
 *
 * Multi-page evidence review interface for SORA evidence extraction.
 * Integrates ReviewNavigation with four content pages:
 * - Page 1: Ground Risk Answers
 * - Page 2: Ground Risk Mitigations
 * - Page 3: Air Risk Answers
 * - Page 4: Air Risk Mitigations
 *
 * Handles page routing, state preservation, citation panel display,
 * and feedback form display.
 *
 * Requirements: 10.1, 10.2
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftRegular,
  Warning24Regular,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { useExtraction, PAGE_LABELS, TOTAL_PAGES } from '../../contexts/ExtractionContext';
import { ReviewNavigation } from '../../components/ReviewNavigation';
import { GroundRiskAnswers } from '../../components/GroundRiskAnswers';
import { GroundRiskMitigations } from '../../components/GroundRiskMitigations';
import { AirRiskAnswers } from '../../components/AirRiskAnswers';
import { AirRiskMitigations } from '../../components/AirRiskMitigations';
import { CitationPanel } from '../../components/CitationPanel';
import { FeedbackForm } from '../../components/FeedbackForm';
import type { Citation, FeedbackSubmission, Feedback, FeedbackItemType } from '../../types';
import styles from './ReviewPage.module.css';

// ============================================================================
// Types
// ============================================================================

export interface ReviewPageProps {
  /** Extraction ID from route params (optional, can be passed directly) */
  extractionId?: string;
  /** Initial page number (1-4) */
  initialPage?: number;
}

interface FeedbackFormState {
  isOpen: boolean;
  itemId: string | null;
  itemType: 'evidence' | 'mitigation';
  /** The specific feedback item type for storage (Requirement 8.3) */
  feedbackItemType: FeedbackItemType;
  currentValue: string;
  initialFeedback?: Partial<FeedbackSubmission>;
}

const INITIAL_FEEDBACK_STATE: FeedbackFormState = {
  isOpen: false,
  itemId: null,
  itemType: 'evidence',
  feedbackItemType: 'ground_risk_answer',
  currentValue: '',
};

// ============================================================================
// Component
// ============================================================================

/**
 * ReviewPage - Multi-page evidence review interface
 *
 * Features:
 * - ReviewNavigation component at the top with page tabs and progress
 * - Dynamic content rendering based on current page (1-4)
 * - Citation panel display when citations are clicked
 * - Feedback form display when feedback buttons are clicked
 * - State preservation across page navigation (Req 10.2)
 * - Progress tracking for completed pages (Req 10.3)
 */
export const ReviewPage: React.FC<ReviewPageProps> = ({
  extractionId: propExtractionId,
  initialPage,
}) => {
  const { extractionId: routeExtractionId, page: routePage } = useParams<{
    extractionId?: string;
    page?: string;
  }>();
  const navigate = useNavigate();

  // Determine extraction ID from props or route
  const extractionId = propExtractionId || routeExtractionId;

  // Extraction context
  const {
    currentExtraction,
    reviewProgress,
    pendingFeedback,
    storedFeedback,
    isLoading,
    error,
    loadExtraction,
    addFeedback,
    markPageComplete,
    navigateToPage,
    saveFeedback,
    clearError,
  } = useExtraction();

  // Local state for citation panel
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);

  // Local state for feedback form
  const [feedbackFormState, setFeedbackFormState] = useState<FeedbackFormState>(
    INITIAL_FEEDBACK_STATE
  );
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Local state for completion
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Determine current page from route or context
  const currentPage = useMemo(() => {
    if (routePage) {
      const pageNum = parseInt(routePage, 10);
      if (pageNum >= 1 && pageNum <= TOTAL_PAGES) {
        return pageNum;
      }
    }
    if (initialPage && initialPage >= 1 && initialPage <= TOTAL_PAGES) {
      return initialPage;
    }
    return reviewProgress.currentPage;
  }, [routePage, initialPage, reviewProgress.currentPage]);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Load extraction data when component mounts or extractionId changes
   */
  useEffect(() => {
    if (extractionId && !currentExtraction) {
      loadExtraction(extractionId);
    }
  }, [extractionId, currentExtraction, loadExtraction]);

  /**
   * Sync context page with route page
   */
  useEffect(() => {
    if (currentPage !== reviewProgress.currentPage) {
      navigateToPage(currentPage);
    }
  }, [currentPage, reviewProgress.currentPage, navigateToPage]);

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Handle navigation between pages
   * Updates both context state and URL route
   */
  const handleNavigate = useCallback(
    (page: number) => {
      navigateToPage(page);
      if (extractionId) {
        navigate(`/evidence-extractor/review/${extractionId}/${page}`, { replace: true });
      }
    },
    [extractionId, navigate, navigateToPage]
  );

  /**
   * Handle citation click - open citation panel
   */
  const handleCitationClick = useCallback((citation: Citation) => {
    setSelectedCitation(citation);
    setIsCitationPanelOpen(true);
  }, []);

  /**
   * Handle citation panel close
   */
  const handleCloseCitationPanel = useCallback(() => {
    setIsCitationPanelOpen(false);
    // Keep citation data briefly for animation
    setTimeout(() => {
      setSelectedCitation(null);
    }, 300);
  }, []);

  /**
   * Handle feedback button click - open feedback form
   * Determines item type and current value based on the item ID
   * Sets the correct FeedbackItemType for storage (Requirement 8.3)
   */
  const handleFeedbackClick = useCallback(
    (itemId: string) => {
      if (!currentExtraction) return;

      // Find the item and determine its type and current value
      let itemType: 'evidence' | 'mitigation' = 'evidence';
      let feedbackItemType: FeedbackItemType = 'ground_risk_answer';
      let currentValue = '';
      let existingFeedback: Partial<FeedbackSubmission> | undefined;

      // Check ground risk answers
      const groundRiskAnswer = currentExtraction.groundRiskAnswers.find(
        (a) => a.id === itemId
      );
      if (groundRiskAnswer) {
        itemType = 'evidence';
        feedbackItemType = 'ground_risk_answer';
        currentValue = groundRiskAnswer.extractedValue;
      }

      // Check air risk answers
      const airRiskAnswer = currentExtraction.airRiskAnswers.find(
        (a) => a.id === itemId
      );
      if (airRiskAnswer) {
        itemType = 'evidence';
        feedbackItemType = 'air_risk_answer';
        currentValue = String(airRiskAnswer.extractedValue);
      }

      // Check ground risk mitigations
      const groundRiskMitigation = currentExtraction.groundRiskMitigations.find(
        (m) => m.id === itemId
      );
      if (groundRiskMitigation) {
        itemType = 'mitigation';
        feedbackItemType = 'ground_risk_mitigation';
        currentValue = groundRiskMitigation.selectedLevel;
      }

      // Check air risk mitigations
      const airRiskMitigation = currentExtraction.airRiskMitigations.find(
        (m) => m.id === itemId
      );
      if (airRiskMitigation) {
        itemType = 'mitigation';
        feedbackItemType = 'air_risk_mitigation';
        currentValue = airRiskMitigation.selectedValue ? 'Yes' : 'No';
      }

      // Check for existing pending feedback
      const pending = pendingFeedback.get(itemId);
      if (pending) {
        existingFeedback = pending;
      }

      setFeedbackFormState({
        isOpen: true,
        itemId,
        itemType,
        feedbackItemType,
        currentValue,
        initialFeedback: existingFeedback,
      });
    },
    [currentExtraction, pendingFeedback]
  );

  /**
   * Handle feedback form submission
   * Stores feedback with timestamp and user identifier (Requirements 8.2, 8.3)
   */
  const handleFeedbackSubmit = useCallback(
    (feedback: FeedbackSubmission) => {
      setIsSubmittingFeedback(true);

      try {
        // Add feedback to pending feedback in context with itemType and originalValue
        // This ensures feedback is stored with proper metadata (Requirement 8.3)
        addFeedback(
          feedback,
          feedbackFormState.feedbackItemType,
          feedbackFormState.currentValue
        );

        // Close the form
        setFeedbackFormState(INITIAL_FEEDBACK_STATE);
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [addFeedback, feedbackFormState.feedbackItemType, feedbackFormState.currentValue]
  );

  /**
   * Handle feedback form cancel
   */
  const handleFeedbackCancel = useCallback(() => {
    setFeedbackFormState(INITIAL_FEEDBACK_STATE);
  }, []);

  /**
   * Handle marking current page as complete
   */
  const handleMarkPageComplete = useCallback(() => {
    markPageComplete(currentPage);
  }, [currentPage, markPageComplete]);

  /**
   * Handle review completion
   * Saves all pending feedback and navigates to completion
   */
  const handleComplete = useCallback(async () => {
    setIsCompleting(true);
    setCompletionError(null);

    try {
      // Save all pending feedback
      await saveFeedback();

      // Navigate to documents page or show completion message
      navigate('/evidence-extractor');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to complete review. Please try again.';
      setCompletionError(errorMessage);
    } finally {
      setIsCompleting(false);
    }
  }, [saveFeedback, navigate]);

  /**
   * Handle retry loading extraction
   */
  const handleRetry = useCallback(() => {
    clearError();
    if (extractionId) {
      loadExtraction(extractionId);
    }
  }, [clearError, extractionId, loadExtraction]);

  // ============================================================================
  // Memoized Values
  // ============================================================================

  /**
   * Create feedback map for mitigations from stored feedback
   * Uses storedFeedback which contains proper timestamp and user identifier (Requirement 8.3)
   */
  const feedbackMap = useMemo(() => {
    // storedFeedback already contains full Feedback objects with timestamp and user identifier
    return storedFeedback;
  }, [storedFeedback]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  /**
   * Render the appropriate content component based on current page
   */
  const renderPageContent = () => {
    if (!currentExtraction) {
      return (
        <div className={styles.placeholder}>
          <p className={styles.placeholderText}>
            No extraction data available. Please process your documents first.
          </p>
        </div>
      );
    }

    switch (currentPage) {
      case 1:
        return (
          <GroundRiskAnswers
            answers={currentExtraction.groundRiskAnswers}
            onCitationClick={handleCitationClick}
            onFeedbackClick={handleFeedbackClick}
            isLoading={isLoading}
          />
        );

      case 2:
        return (
          <GroundRiskMitigations
            mitigations={currentExtraction.groundRiskMitigations}
            onCitationClick={handleCitationClick}
            onFeedbackClick={handleFeedbackClick}
            feedbackMap={feedbackMap}
            isLoading={isLoading}
          />
        );

      case 3:
        return (
          <AirRiskAnswers
            answers={currentExtraction.airRiskAnswers}
            onCitationClick={handleCitationClick}
            onFeedbackClick={handleFeedbackClick}
            isLoading={isLoading}
          />
        );

      case 4:
        return (
          <AirRiskMitigations
            mitigations={currentExtraction.airRiskMitigations}
            onCitationClick={handleCitationClick}
            onFeedbackClick={handleFeedbackClick}
            feedbackMap={feedbackMap}
            isLoading={isLoading}
          />
        );

      default:
        return (
          <div className={styles.placeholder}>
            <p className={styles.placeholderText}>Invalid page number</p>
          </div>
        );
    }
  };

  // ============================================================================
  // Loading State
  // ============================================================================

  if (isLoading && !currentExtraction) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <ArrowSyncRegular className={styles.spinner} />
          <p className={styles.loadingText}>Loading extraction results...</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Error State
  // ============================================================================

  if (error && !currentExtraction) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <Warning24Regular className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Unable to Load Extraction</h2>
          <p className={styles.errorText}>{error.message}</p>
          <div className={styles.errorActions}>
            <button className={styles.retryButton} onClick={handleRetry}>
              Try Again
            </button>
            <Link to="/evidence-extractor" className={styles.backButton}>
              Back to Documents
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <Link to="/evidence-extractor" className={styles.backLink}>
          <ArrowLeftRegular className={styles.backIcon} />
          <span>Back to Documents</span>
        </Link>
        <h1 className={styles.title}>Review Extracted Evidence</h1>
        <p className={styles.subtitle}>
          Review and validate the SORA evidence extracted from your documents.
          {currentExtraction && (
            <> Extraction ID: {currentExtraction.processId}</>
          )}
        </p>
      </header>

      {/* Review Navigation */}
      <ReviewNavigation
        currentPage={currentPage}
        totalPages={TOTAL_PAGES}
        pageLabels={PAGE_LABELS}
        completedPages={reviewProgress.completedPages}
        onNavigate={handleNavigate}
        onComplete={handleComplete}
        hasUnsavedChanges={reviewProgress.hasUnsavedChanges}
      />

      {/* Main Content Area */}
      <main className={styles.content}>
        {/* Page Content */}
        <div className={styles.pageContent}>
          {renderPageContent()}
        </div>

        {/* Mark Page Complete Button */}
        {currentExtraction && !reviewProgress.completedPages.includes(currentPage) && (
          <div className={styles.pageActions}>
            <button
              className={styles.markCompleteButton}
              onClick={handleMarkPageComplete}
            >
              Mark Page as Reviewed
            </button>
          </div>
        )}

        {/* Completion Error */}
        {completionError && (
          <div className={styles.completionError}>
            <Warning24Regular className={styles.completionErrorIcon} />
            <span>{completionError}</span>
          </div>
        )}
      </main>

      {/* Citation Panel */}
      <CitationPanel
        citation={selectedCitation}
        isOpen={isCitationPanelOpen}
        onClose={handleCloseCitationPanel}
      />

      {/* Feedback Form Modal */}
      {feedbackFormState.isOpen && feedbackFormState.itemId && (
        <div
          className={styles.feedbackOverlay}
          onClick={handleFeedbackCancel}
        >
          <div className={styles.feedbackModal} onClick={(e) => e.stopPropagation()}>
            <FeedbackForm
              itemId={feedbackFormState.itemId}
              itemType={feedbackFormState.itemType}
              currentValue={feedbackFormState.currentValue}
              onSubmit={handleFeedbackSubmit}
              onCancel={handleFeedbackCancel}
              initialFeedback={feedbackFormState.initialFeedback}
              isSubmitting={isSubmittingFeedback}
            />
          </div>
        </div>
      )}

      {/* Completing Overlay */}
      {isCompleting && (
        <div className={styles.completingOverlay}>
          <div className={styles.completingContent}>
            <ArrowSyncRegular className={styles.spinner} />
            <p>Saving feedback and completing review...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPage;
